import type { AutoCompleteApiObject } from '@/queue/ducks/slice';
import type { TransferFilterModel } from '@/queue/types';
import { TransferFilterModelInitialValue } from '@/queue/utils';

/**
 * Stored inside transfer-queue `search_filters` JSON alongside ag-grid filter fields.
 * Stripped before the object is merged into Redux `transferFilterModel` (never sent to ag-grid).
 */
export const TRANSFER_QUEUE_UI_META_SELLER_SELECTIONS =
  '__transferQueueUiMetaSellerSelections' as const;

type AnyRecord = Record<string, unknown>;

function isAutoCompleteSelection(x: unknown): x is AutoCompleteApiObject {
  return (
    !!x &&
    typeof x === 'object' &&
    typeof (x as AutoCompleteApiObject).shortLicenseNumber === 'string'
  );
}

/** Remove UI-only keys from persisted search_filters. */
export function stripTransferQueueSearchFiltersUiMeta(
  searchFilters: AnyRecord
): AnyRecord {
  const copy = { ...searchFilters };
  delete copy[TRANSFER_QUEUE_UI_META_SELLER_SELECTIONS];
  return copy;
}

/**
 * Split API `search_filters` into ag-grid model + seller multi-select options for chips/dropdown.
 */
export function sanitizeSearchFiltersForTransferModel(
  searchFilters: AnyRecord
): { model: TransferFilterModel; sellerSelections: AutoCompleteApiObject[] } {
  const raw = { ...searchFilters };
  const sellerRaw = raw[TRANSFER_QUEUE_UI_META_SELLER_SELECTIONS];
  delete raw[TRANSFER_QUEUE_UI_META_SELLER_SELECTIONS];

  const sellerSelections = Array.isArray(sellerRaw)
    ? sellerRaw.filter(isAutoCompleteSelection)
    : [];

  return {
    model: {
      ...TransferFilterModelInitialValue,
      ...raw,
    } as TransferFilterModel,
    sellerSelections,
  };
}

/** Build payload for PUT /dealers/filter/collections (persists seller labels for reload). */
export function buildTransferQueueSearchFiltersPayload(
  transferFilterModel: TransferFilterModel,
  sellerLicenseKeyLists: AutoCompleteApiObject[]
): AnyRecord {
  const sellerValues =
    ((transferFilterModel.sellerLicenseKey as { values?: string[] })?.values ??
      []) as string[];
  const persistedSelections = sellerLicenseKeyLists.filter((s) =>
    sellerValues.includes(s.shortLicenseNumber)
  );
  return {
    ...transferFilterModel,
    [TRANSFER_QUEUE_UI_META_SELLER_SELECTIONS]: persistedSelections,
  };
}
